import { authApi } from '@shared/api-client';
import * as React from 'react';
import { Link, useNavigate } from 'react-router-dom';
import { Alert } from '@/components/ui/alert';
import { Button } from '@/components/ui/button';
import { Card, CardContent, CardHeader, CardTitle } from '@/components/ui/card';
import { Field } from '@/components/ui/field';
import { Input } from '@/components/ui/input';
import { useFormError } from '@/hooks/use-form-errors';

export const ForgotPasswordPage = () => {
  const navigate = useNavigate();
  const { error, captureError, clearError } = useFormError();
  const [isSubmitting, setIsSubmitting] = React.useState(false);

  const handleSubmit = async (event: React.FormEvent<HTMLFormElement>) => {
    event.preventDefault();
    clearError();
    setIsSubmitting(true);

    const email = String(new FormData(event.currentTarget).get('email'));

    try {
      await authApi.forgotPassword({ email });

      // The API answers the same way whether or not the account exists, and the
      // UI must not undo that by behaving differently.
      navigate(`/reset-password?email=${encodeURIComponent(email)}`);
    } catch (err) {
      captureError(err);
    } finally {
      setIsSubmitting(false);
    }
  };

  return (
    <Card>
      <CardHeader>
        <CardTitle className="text-base">Reset your password</CardTitle>
      </CardHeader>

      <CardContent>
        <form onSubmit={handleSubmit} className="space-y-4">
          {error ? <Alert tone="error">{error}</Alert> : null}

          <Field
            label="Email"
            htmlFor="email"
            hint="If an account exists for this address, we will send a reset code."
          >
            <Input
              name="email"
              type="email"
              autoComplete="email"
              required
              autoFocus
              placeholder="you@company.com"
            />
          </Field>

          <Button type="submit" className="w-full" loading={isSubmitting}>
            Send reset code
          </Button>
        </form>

        <p className="mt-5 text-center text-sm">
          <Link
            to="/signin"
            className="text-ink-muted underline-offset-4 hover:text-ink hover:underline"
          >
            Back to sign in
          </Link>
        </p>
      </CardContent>
    </Card>
  );
};
